import styled from '@emotion/styled'
import gql from 'graphql-tag'
import React, { useContext, useState } from 'react'
import { useQuery } from '../../../../api/graphql'
import { CurrentUserContext } from '../../../../providers/CurrentUserContext'
import OrganizationRow from './OrganizationRow'
import PendingOrganizationRow from './PendingOrganizationRow'
import MigrateEventsModal from './MigrateEventsModal'
import PendingInvitationsSection from './PendingInvitationsSection'
import { Hugeicon } from '../../../../icons/Hugeicon'

export const OrganizationMembershipsQuery = gql`
  query OrganizationMembershipsQuery {
    currentUser {
      id
      personalEventsCount
      organizationMemberships {
        id
        role
        organization {
          id
          name
          slug
          organizationType
          logoPublicId
          memberCount
          contactEmail
          phone
          websiteUrl
          streetAddress
          city
          state
          zip
          hasAssociatedEvents
          hasActiveSubscription
          organizationMemberships {
            id
            role
            roleTitle
            pending
            displayName
            displayEmail
            invitedEmail
            invitedName
            user {
              id
              firstName
              lastName
              email
              profilePicPublicId
            }
          }
        }
      }
      pendingOrganizationInvitations {
        id
        invitedEmail
        invitedName
        invitedAt
        expiresAt
        organization {
          id
          name
          slug
          organizationType
          logoPublicId
          memberCount
        }
      }
    }
  }
`

export type OrganizationMember = {
  id: string
  role: 'owner' | 'member'
  roleTitle: string | null
  pending: boolean
  displayName: string
  displayEmail: string | null
  invitedEmail: string | null
  invitedName: string | null
  user: {
    id: string
    firstName: string | null
    lastName: string | null
    email: string | null
    profilePicPublicId: string | null
  } | null
}

export type Organization = {
  id: string
  name: string
  slug: string
  organizationType: string | null
  logoPublicId: string | null
  memberCount: number
  contactEmail: string | null
  phone: string | null
  websiteUrl: string | null
  streetAddress: string | null
  city: string | null
  state: string | null
  zip: string | null
  hasAssociatedEvents: boolean
  hasActiveSubscription: boolean
  organizationMemberships: OrganizationMember[]
}

export type OrganizationMembership = {
  id: string
  role: 'owner' | 'member'
  organization: Organization
}

const OrganizationsSection: React.FC = () => {
  const { data, loading, refetch } = useQuery(OrganizationMembershipsQuery)
  const { currentUser } = useContext(CurrentUserContext)
  const [showPendingOrg, setShowPendingOrg] = useState(false)
  const [migrateModalOrg, setMigrateModalOrg] = useState<{ id: string; name: string } | null>(null)

  if (loading) return null

  const memberships: OrganizationMembership[] = data?.currentUser?.organizationMemberships || []
  const pendingInvitations = data?.currentUser?.pendingOrganizationInvitations || []
  const personalEventsCount: number = data?.currentUser?.personalEventsCount ?? 0

  const handleAddOrganization = () => {
    setShowPendingOrg(true)
  }

  const handleCancelPendingOrg = () => {
    setShowPendingOrg(false)
  }

  const showMigrateModalIfNeeded = (org: { id: string; name: string }) => {
    refetch().then((result) => {
      const count = result.data?.currentUser?.personalEventsCount ?? 0
      if (count > 0) {
        setMigrateModalOrg(org)
      }
    })
  }

  const handleCreateSuccess = (org: { id: string; name: string }) => {
    setShowPendingOrg(false)
    showMigrateModalIfNeeded(org)
  }

  return (
    <SectionContainer>
      {pendingInvitations.length > 0 && (
        <InvitationsWrapper>
          <PendingInvitationsSection
            invitations={pendingInvitations}
            onAccept={(org) => showMigrateModalIfNeeded(org)}
            onDecline={refetch}
          />
        </InvitationsWrapper>
      )}

      <SectionHeader>
        <SectionHeaderLeft>
          <SectionTitle>Organizations</SectionTitle>
          <OrgCount>{memberships.length} {memberships.length === 1 ? 'organization' : 'organizations'}</OrgCount>
        </SectionHeaderLeft>
        <AddButton onClick={handleAddOrganization} disabled={showPendingOrg}>
          <PlusIcon />
          Add Organization
        </AddButton>
      </SectionHeader>

      <OrganizationsList>
        {showPendingOrg && (
          <PendingOrganizationRow
            currentUserId={currentUser?.id}
            onCancel={handleCancelPendingOrg}
            onCreateSuccess={handleCreateSuccess}
          />
        )}
        {memberships.length === 0 && !showPendingOrg ? (
          <EmptyState>
            <EmptyIcon>
              <BuildingIcon />
            </EmptyIcon>
            <EmptyTitle>No organizations yet</EmptyTitle>
            <EmptyText>Create an organization to start managing events and team members.</EmptyText>
          </EmptyState>
        ) : (
          memberships.map(membership => (
            <OrganizationRow
              key={membership.id}
              membership={membership}
              currentUserId={currentUser?.id}
              onDeleteSuccess={refetch}
            />
          ))
        )}
      </OrganizationsList>
      {migrateModalOrg && (
        <MigrateEventsModal
          isOpen={!!migrateModalOrg}
          onClose={() => setMigrateModalOrg(null)}
          organizationId={migrateModalOrg.id}
          organizationName={migrateModalOrg.name}
          personalEventsCount={personalEventsCount}
        />
      )}
    </SectionContainer>
  )
}

export default OrganizationsSection

const BuildingIcon: React.FC = () => <Hugeicon name="bank" size={20} />

const PlusIcon: React.FC = () => <Hugeicon name="plus" size={16} />

const SectionContainer = styled.div``

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`

const SectionHeaderLeft = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
`

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #334155;
  margin: 0;
`

const OrgCount = styled.span`
  font-size: 13px;
  color: #94a3b8;
`

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: white;
  color: #334155;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const OrganizationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const EmptyState = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 48px 32px;
  text-align: center;
`

const EmptyIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  margin: 0 auto 16px;
`

const EmptyTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #334155;
  margin: 0 0 6px;
`

const EmptyText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
`

const InvitationsWrapper = styled.div`
  margin-bottom: 24px;
`
